import {
  Users,
  BookOpen,
  FileText,
  Building,
  Calendar,
  MessageSquare,
  CreditCard,
  GraduationCap,
  Settings,
  ListChecks,
  BarChart3,
  ShieldCheck,
  Activity,
  FolderOpen,
} from 'lucide-react';

class ModuleRegistry {
  constructor() {
    this.modules = new Map();
    this.initialized = false;
  }

  /**
   * Initialize the module registry with all available modules
   */
  init() {
    if (this.initialized) return;


    // Register productivity coaching module
    this.registerModule({
      id: 'productivity-coaching',
      name: 'Productivity Coaching',
      description: 'Real estate agent coaching and management',
      version: '1.0.0',
      icon: Users,
      color: 'green',
      status: 'active',
      category: 'business',
      routes: [
        { path: '/productivity-coaching', component: 'ProductivityCoachingDashboard', exact: true },
        { path: '/agents', component: 'Agents' },
        { path: '/agents/:id', component: 'AgentDetail' },
        { path: '/accountability', component: 'AccountabilityGrid', exact: true },
        { path: '/pipeline', component: 'Pipeline' },
        { path: '/pipeline/:id', component: 'PipelineDetail' },
        { path: '/communications', component: 'Communications' },
        { path: '/communications/:id', component: 'CommunicationDetail' },
        { path: '/communications/analytics', component: 'CommunicationAnalyticsDashboard' },
        { path: '/classes', component: 'ClassSchedule' },
        { path: '/instructors', component: 'InstructorDirectoryAdmin' },
        { path: '/classes/templates', component: 'Classes' },
        { path: '/classes/schedule/launch-series', component: 'LaunchSeries' },
        { path: '/classes/events/:id', component: 'ClassEventDetail' },
        { path: '/instructors/:id', component: 'InstructorDetail' },
        { path: '/tasks', component: 'TasksPage' },
        // Plans section (Task Plans + Learning Paths)
        { path: '/plans', component: 'PlansPage', exact: true },
        { path: '/plans/task-plans/create', component: 'CreateTaskPlan' },
        { path: '/plans/task-plans/:id', component: 'TaskPlanDetails' },
        { path: '/plans/task-plans/:id/edit', component: 'EditTaskPlan' },
        { path: '/plans/task-plans/:id/assignments', component: 'PlanAssignments' },
        { path: '/plans/learning-paths/create', component: 'CreateLearningPath' },
        { path: '/plans/learning-paths/:id', component: 'LearningPathDetails' },
        { path: '/plans/learning-paths/:id/edit', component: 'EditLearningPath' },
        { path: '/plans/learning-paths/progress/:agentId', component: 'AgentLearningProgress' },
        { path: '/plans/learning-paths/reviews', component: 'EvidenceReviewQueue' },
        // Legacy routes for backwards compatibility
        { path: '/task-plans', component: 'PlansPage' },
        { path: '/task-plans/create', component: 'CreateTaskPlan' },
        { path: '/task-plans/:id', component: 'TaskPlanDetails' },
        { path: '/task-plans/:id/edit', component: 'EditTaskPlan' },
        { path: '/task-plans/:id/assignments', component: 'PlanAssignments' },
        { path: '/task-analytics', component: 'TaskAnalyticsDashboard' },
        { path: '/resources', component: 'ResourcesPage' },
        { path: '/resources/:id', component: 'ResourceDetailPage' },
        { path: '/referrals', component: 'Referrals' }
      ],
      navigation: [
        { name: 'Agents', href: '/agents', icon: Users },
        { name: 'Pipeline', href: '/pipeline', icon: CreditCard },
        { name: 'Classes', href: '/classes', icon: Calendar },
        { name: 'Instructors', href: '/instructors', icon: GraduationCap },
        { name: 'Communications', href: '/communications', icon: MessageSquare },
        { name: 'Resources', href: '/resources', icon: FolderOpen },
        { name: 'Analytics', href: '/communications/analytics', icon: BarChart3 },
        { name: 'Tasks', href: '/tasks', icon: ListChecks }
      ],
      permissions: ['coaching:read', 'coaching:write', 'coaching:delete'],
      dependencies: [],
      apis: [
        '/api/agents',
        '/api/transactions',


        '/api/communications',
        '/api/classes',
        '/api/classEvents',
        '/api/instructors'
      ],
      settings: {
        autoFollowUpDays: 30,
        milestoneTracking: true,
        transactionThreshold: 1000,
        enableNotifications: true
      },
      integrations: {
        gps: {
          taskCreation: true,
          progressTracking: true,
          recommendations: true
        }
      }
    });


    this.registerModule({
      id: 'super-admin',
      name: 'Super Admin',
      description: 'Platform-wide controls for global administrators',
      version: '1.0.0',
      icon: ShieldCheck,
      color: 'slate',
      status: 'active',
      category: 'administration',
      routes: [
        { path: '/super-admin', component: 'SuperAdminDashboard', exact: true },
        { path: '/super-admin/dashboard', component: 'SuperAdminDashboard' },
        { path: '/super-admin/overview', component: 'SuperAdminOverview' },
        { path: '/super-admin/organizations/:id', component: 'SuperAdminOrganizationDetail' },
        { path: '/super-admin/organizations', component: 'SuperAdminOrganizations' },
        { path: '/super-admin/users/:id', component: 'SuperAdminUserDetail' },
        { path: '/super-admin/users', component: 'SuperAdminUsers' },
        { path: '/super-admin/usage', component: 'SuperAdminUsage' }
      ],
      navigation: [
        { name: 'Dashboard', href: '/super-admin/dashboard', icon: BarChart3 },
        { name: 'Overview', href: '/super-admin/overview', icon: Activity },
        { name: 'Organizations', href: '/super-admin/organizations', icon: Building },
        { name: 'Users', href: '/super-admin/users', icon: Users },
        { name: 'Health', href: '/super-admin/usage', icon: Activity }
      ],
      permissions: ['superadmin:read', 'superadmin:write'],
      dependencies: [],
      apis: [
        '/api/super-admin/dashboard',
        '/api/super-admin/usage',
        '/api/super-admin/organizations',
        '/api/super-admin/users'
      ],
      settings: {
        includeInactiveByDefault: false,
        maxOrganizations: 1000,
        auditLogRetentionDays: 90
      }
    });


    this.registerModule({
      id: 'learning',
      name: 'Learning',
      description: 'Knowledge tracking and skill development',
      version: '0.1.0',
      icon: BookOpen,
      color: 'purple',
      status: 'inactive',
      category: 'personal',
      routes: [
        { path: '/learning', component: 'LearningDashboard', exact: true }
      ],
      navigation: [
        { name: 'Courses', href: '/learning/courses', icon: BookOpen },
        { name: 'Progress', href: '/learning/progress', icon: BookOpen },
        { name: 'Certificates', href: '/learning/certificates', icon: BookOpen }
      ],
      permissions: ['learning:read', 'learning:write'],
      dependencies: ['gps'],
      apis: ['/api/learning'],
      settings: {
        trackingEnabled: true,
        certificateGeneration: true,
        progressReminders: true
      }
    });

    this.registerModule({
      id: 'content-creation',
      name: 'Content Creation',
      description: 'Content planning, creation, and publishing',
      version: '0.1.0',
      icon: FileText,
      color: 'pink',
      status: 'inactive',
      category: 'business',
      routes: [
        { path: '/content', component: 'ContentDashboard', exact: true }
      ],
      navigation: [
        { name: 'Calendar', href: '/content/calendar', icon: Calendar },
        { name: 'Posts', href: '/content/posts', icon: FileText },
        { name: 'Analytics', href: '/content/analytics', icon: FileText }
      ],
      permissions: ['content:read', 'content:write', 'content:publish'],
      dependencies: ['gps'],
      apis: ['/api/content'],
      settings: {
        autoPublishing: false,
        platformIntegrations: ['linkedin', 'twitter'],
        contentApproval: true
      }
    });

    this.registerModule({
      id: 'real-estate',
      name: 'Real Estate',
      description: 'Property investment and deal pipeline',
      version: '0.1.0',
      icon: Building,
      color: 'indigo',
      status: 'inactive',
      category: 'business',
      routes: [
        { path: '/real-estate', component: 'RealEstateDashboard', exact: true }
      ],
      navigation: [
        { name: 'Properties', href: '/real-estate/properties', icon: Building },
        { name: 'Deals', href: '/real-estate/deals', icon: Building },
        { name: 'Analysis', href: '/real-estate/analysis', icon: Building }
      ],
      permissions: ['realestate:read', 'realestate:write'],
      dependencies: ['gps', 'finances'],
      apis: ['/api/real-estate'],
      settings: {
        marketAnalysis: true,
        dealTracking: true,
        profitCalculation: true
      }
    });

    this.initialized = true;
  }

  /**
   * Register a new module
   */
  registerModule(moduleConfig) {
    if (!moduleConfig.id) {
      throw new Error('Module must have an ID');
    }

    // Validate required fields
    const required = ['name', 'description', 'version', 'icon', 'status'];
    for (const field of required) {
      if (!moduleConfig[field]) {
        throw new Error(`Module ${moduleConfig.id} missing required field: ${field}`);
      }
    }

    // Set defaults
    const module = {
      category: 'custom',
      color: 'gray',
      routes: [],
      navigation: [],
      permissions: [],
      dependencies: [],
      apis: [],
      settings: {},
      integrations: {},
      ...moduleConfig,
      registeredAt: new Date().toISOString()
    };

    this.modules.set(moduleConfig.id, module);
    return module;
  }

  /**
   * Get all registered modules
   */
  getAllModules() {
    return Array.from(this.modules.values());
  }

  /**
   * Get active modules only
   */
  getActiveModules() {
    return this.getAllModules().filter(module => module.status === 'active');
  }

  /**
   * Get modules by category
   */
  getModulesByCategory(category) {
    return this.getAllModules().filter(module => module.category === category);
  }

  /**
   * Get a specific module by ID
   */
  getModule(id) {
    return this.modules.get(id);
  }

  /**
   * Check if a module exists and is active
   */
  isModuleActive(id) {
    const module = this.getModule(id);
    return module && module.status === 'active';
  }

  /**
   * Get module navigation items for sidebar
   */
  getModuleNavigation() {
    const activeModules = this.getActiveModules();
    const moduleNavigation = activeModules.map(module => ({
      name: module.name,
      icon: module.icon,
      type: 'category',
      key: module.id,
      href: module.routes.find(r => r.exact)?.path || `/${module.id}`,
      items: module.navigation,
      color: module.color
    }));

    // Add Settings as a single item at the end
    moduleNavigation.push({
      name: 'Settings',
      href: '/settings',
      icon: Settings,
      type: 'single'
    });

    return moduleNavigation;
  }

  /**
   * Get all routes for active modules
   */
  getModuleRoutes() {
    const routes = [];
    const activeModules = this.getActiveModules();
    
    for (const module of activeModules) {
      routes.push(...module.routes.map(route => ({
        ...route,
        moduleId: module.id,
        moduleName: module.name
      })));
    }
    
    return routes;
  }

  /**
   * Check module dependencies
   */
  checkDependencies(moduleId) {
    const module = this.getModule(moduleId);
    if (!module) return { valid: false, missing: ['Module not found'] };

    const missing = [];
    for (const depId of module.dependencies) {
      if (!this.isModuleActive(depId)) {
        missing.push(depId);
      }
    }

    return {
      valid: missing.length === 0,
      missing
    };
  }

  /**
   * Get module settings
   */
  getModuleSettings(moduleId) {
    const module = this.getModule(moduleId);
    return module ? module.settings : {};
  }

  /**
   * Update module settings
   */
  updateModuleSettings(moduleId, newSettings) {
    const module = this.getModule(moduleId);
    if (module) {
      module.settings = { ...module.settings, ...newSettings };
      return true;
    }
    return false;
  }

  /**
   * Enable a module
   */
  enableModule(moduleId) {
    const module = this.getModule(moduleId);
    if (!module) return false;

    const depCheck = this.checkDependencies(moduleId);
    if (!depCheck.valid) {
      throw new Error(`Cannot enable module ${moduleId}. Missing dependencies: ${depCheck.missing.join(', ')}`);
    }

    module.status = 'active';
    return true;
  }

  /**
   * Disable a module
   */
  disableModule(moduleId) {
    const module = this.getModule(moduleId);
    if (!module) return false;

    // Check if other modules depend on this one
    const dependents = this.getAllModules().filter(m => 
      m.dependencies.includes(moduleId) && m.status === 'active'
    );

    if (dependents.length > 0) {
      throw new Error(`Cannot disable module ${moduleId}. Required by: ${dependents.map(m => m.name).join(', ')}`);
    }

    module.status = 'inactive';
    return true;
  }

  /**
   * Get module integration capabilities
   */
  getModuleIntegrations(moduleId) {
    const module = this.getModule(moduleId);
    return module ? module.integrations : {};
  }

  /**
   * Generate module manifest for export/import
   */
  generateManifest() {
    return {
      version: '1.0.0',
      generatedAt: new Date().toISOString(),
      modules: this.getAllModules().map(module => ({
        id: module.id,
        name: module.name,
        version: module.version,
        status: module.status,
        settings: module.settings
      }))
    };
  }
}

// Create singleton instance
const moduleRegistry = new ModuleRegistry();

export default moduleRegistry;
